const baseUrl = process.env.REACT_APP_SERVER_URI ?? 'http://localhost:4000';
const normalizedBaseUrl = baseUrl.replace(/\/$/, '');
const AUTH_STORAGE_KEY = 'pninit-user-session';

const buildUrl = (path) => `${normalizedBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;

const getStoredSessionToken = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return JSON.parse(window.localStorage.getItem(AUTH_STORAGE_KEY) || 'null')?.session?.token || '';
  } catch (error) {
    return '';
  }
};

const buildAuthHeaders = () => {
  const token = getStoredSessionToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const requestJson = async (url, options = {}) => {
  const response = await fetch(url, options);
  const payload = await response.json().catch(() => ({}));

  if (!response.ok || payload.success === false) {
    const error = new Error(payload.message ?? `Request failed with status ${response.status}`);
    error.statusCode = response.status;
    throw error;
  }

  return payload.data;
};

export const getRecipes = async ({ page = 1, pageSize = 20, search = '', searchScope = 'title', lang, tags = [] } = {}, signal) => {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (search.trim()) {
    params.set('search', search.trim());
    params.set('searchScope', searchScope);
  }
  if (lang) params.set('lang', lang);
  if (tags.length) params.set('tags', tags.join(','));

  const response = await fetch(buildUrl(`/api/recipes?${params}`), { signal });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const payload = await response.json();

  if (!payload.success) {
    throw new Error('Server responded with an error.');
  }

  // Support both paginated { recipes, totalPages } and legacy flat array
  if (Array.isArray(payload.data)) {
    return { recipes: payload.data, totalPages: 1, total: payload.data.length };
  }
  return {
    recipes: payload.data?.recipes ?? [],
    totalPages: payload.data?.totalPages ?? 1,
    total: payload.data?.total ?? 0,
  };
};

export const getRecipeById = async (id, signal) => {
  if (!id) {
    throw new Error('Recipe identifier is required.');
  }

  const response = await fetch(buildUrl(`/api/recipes/${encodeURIComponent(id)}`), { signal });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const payload = await response.json();

  if (!payload.success) {
    throw new Error('Server responded with an error.');
  }

  return payload.data ?? null;
};

export const createRecipe = async (recipeData, imageFile, signal) => {
  if (!recipeData?.title) {
    throw new Error('Recipe title is required.');
  }

  const formData = new FormData();
  formData.append('payload', JSON.stringify(recipeData));

  if (imageFile) {
    formData.append('image', imageFile);
  }

  const response = await fetch(buildUrl('/api/recipes'), {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: formData,
    signal,
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const payload = await response.json();

  if (!payload.success) {
    throw new Error(payload.message ?? 'Server responded with an error.');
  }

  return payload.data ?? null;
};

export const updateRecipe = async (id, recipeData, imageFile, signal) => {
  if (!id) {
    throw new Error('Recipe identifier is required.');
  }

  const formData = new FormData();
  formData.append('payload', JSON.stringify(recipeData));

  if (imageFile) {
    formData.append('image', imageFile);
  }

  const response = await fetch(buildUrl(`/api/recipes/${encodeURIComponent(id)}`), {
    method: 'PUT',
    headers: buildAuthHeaders(),
    body: formData,
    signal,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.message ?? `Request failed with status ${response.status}`);
  }

  const payload = await response.json();

  if (!payload.success) {
    throw new Error(payload.message ?? 'Server responded with an error.');
  }

  return payload.data ?? null;
};

export const deleteRecipe = async (id, signal) => {
  if (!id) {
    throw new Error('Recipe identifier is required.');
  }

  const response = await fetch(buildUrl(`/api/recipes/${encodeURIComponent(id)}`), {
    method: 'DELETE',
    headers: buildAuthHeaders(),
    signal,
  });

  if (response.status === 204) {
    return true;
  }

  const payload = await response.json().catch(() => ({}));

  if (!response.ok || (payload && payload.success === false)) {
    throw new Error(payload.message ?? `Request failed with status ${response.status}`);
  }

  return true;
};

const recipeService = {
  getRecipes,
  getRecipeById,
  createRecipe,
  updateRecipe,
  deleteRecipe,
};

export const getTopFavorites = async (lang, signal) => {
  const params = new URLSearchParams({ limit: '3' });
  if (lang) params.set('lang', lang);
  const response = await fetch(buildUrl(`/api/recipes/popular?${params}`), { signal });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const payload = await response.json();
  if (!payload.success) throw new Error('Server responded with an error.');
  return payload.data ?? [];
};

export const getUserFavorites = async (signal) => {
  const response = await fetch(buildUrl('/api/users/favorites'), {
    headers: buildAuthHeaders(),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const payload = await response.json();

  if (!payload.success) {
    throw new Error('Server responded with an error.');
  }

  return payload.data ?? [];
};

export const getUserFavoritesPaginated = async ({ page = 1, pageSize = 20, search = '', searchScope = 'title' } = {}, signal) => {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (search.trim()) {
    params.set('search', search.trim());
    params.set('searchScope', searchScope);
  }

  const response = await fetch(buildUrl(`/api/users/favorites?${params}`), {
    headers: buildAuthHeaders(),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const payload = await response.json();

  if (!payload.success) {
    throw new Error('Server responded with an error.');
  }

  // Support both paginated { favorites, totalPages } and legacy flat array
  if (Array.isArray(payload.data)) {
    return { favorites: payload.data, totalPages: 1, total: payload.data.length };
  }
  return {
    favorites: payload.data?.favorites ?? [],
    totalPages: payload.data?.totalPages ?? 1,
    total: payload.data?.total ?? 0,
  };
};

export const addToFavorites = async (recipeId) => {
  const response = await fetch(buildUrl(`/api/users/favorites/${encodeURIComponent(recipeId)}`), {
    method: 'POST',
    headers: buildAuthHeaders(),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok || payload.success === false) {
    throw new Error(payload.message ?? `Request failed with status ${response.status}`);
  }

  return payload.data ?? [];
};

export const removeFromFavorites = async (recipeId) => {
  const response = await fetch(buildUrl(`/api/users/favorites/${encodeURIComponent(recipeId)}`), {
    method: 'DELETE',
    headers: buildAuthHeaders(),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok || payload.success === false) {
    throw new Error(payload.message ?? `Request failed with status ${response.status}`);
  }

  return payload.data ?? [];
};

export const generateFromIngredients = async ({ ingredients, lang }, signal) => {
  if (!Array.isArray(ingredients)) {
    throw new Error('Ingredients list is required.');
  }

  return requestJson(buildUrl('/api/recipes/generate-from-ingredients'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...buildAuthHeaders(),
    },
    body: JSON.stringify({ ingredients, lang }),
    signal,
  });
};

export const saveGeneratedRecipe = async (payload, signal) =>
  requestJson(buildUrl('/api/recipes/save-generated'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...buildAuthHeaders(),
    },
    body: JSON.stringify(payload),
    signal,
  });

export default recipeService;
